import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  ArrowForward,
  ArrowBack,
  Launch,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { upgradePlan } from '../services/planUpgradeService';
import PlansCard from '../components/PlansCard';
import { APP_NAME, STORAGE_KEYS, PLATFORM_NAME, DISABLE_SUBSCRIPTION } from '../helpers/constants';
import { fetchPlans, getMySubscription, Plan as ApiPlan } from '../services/companyService';
import { setMySubscriptionData, getMySubscriptionData, getZohoSubscriptionId, getLoggedInUserData, getCompanyId, type MySubscriptionStoredData } from '../helpers/storageUtils';
import { ChatService } from '../components/ChatService';

interface OnboardingData {
  customerId: string;
  companyName: string;
  contactEmail: string;
  token?: string;
  planId?: string;
  planName?: string;
  planCode?: string;
  isDefaultPlan?: boolean;
  accessToken?: string;
  userId?: string;
  companyId?: string;
}

// Default plans data - will be replaced with actual API call
const DEFAULT_PLANS: ApiPlan[] = [];

interface Plan extends ApiPlan {}

const Onboarding: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [activeStep, setActiveStep] = useState(0);
  const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>(DEFAULT_PLANS);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  /** Connect Chat service step: true once at least one token is saved (mandatory step) */
  const [hasChatServiceToken, setHasChatServiceToken] = useState(false);
  /** Bumped after reactivate refreshes mySubscription so PlansCard re-reads status (localStorage does not trigger renders) */
  const [subscriptionUiVersion, setSubscriptionUiVersion] = useState(0);
  
  // Derived from live onboardingData so it self-corrects when the subscription fetch
  // resets isDefaultPlan to false — avoids the stale one-shot snapshot problem.
  // sessionStorage flag covers PROD where the backend returns isDefaultPlan:false for new
  // users AND where getMySubscription succeeds (auto-assigned free plan) and would
  // otherwise reset isDefaultPlan to false before the user has consciously chosen a plan.
  const isNewUserOnboarding =
    sessionStorage.getItem('isNewSignup') === 'true' || onboardingData?.isDefaultPlan === true;

  // Add a ref to prevent multiple API calls
  const hasFetchedSubscription = useRef(false);

  // When subscription disabled: Welcome + Connect Chat service; else Welcome + Choose Plan
  const steps = DISABLE_SUBSCRIPTION ? ['Welcome', 'Connect Chat service'] : ['Welcome', 'Choose Plan'];

  useEffect(() => {
    
    const storedData = localStorage.getItem('onboardingData');
    
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        setOnboardingData(data);
        if (data.planId) {
          setSelectedPlan(data.planId);
        }
      } catch (error) {
        console.error('Error parsing onboarding data:', error);
        showError('Invalid onboarding data. Please start from the signup page.');
        navigate('/companysignup');
      }
    } else {
      if (user?.email) {
        // PR #685 review (L5) — this fallback's customerId used to be `user.id`, a USER id, which
        // reaches upgradePlan() as a non-empty value and therefore skips that service's own
        // `|| userData.company_id` rescue. Preferring the real company id (getCompanyId(), already
        // used by this same file's PlansCard companyId prop below) means a plan-change/payment
        // request from this fallback uses the actual company, not the individual user, as the
        // Zoho customer — user.id stays only as the very last resort if no company id is set yet.
        const dynamicOnboardingData = {
          customerId: getCompanyId() || user.id || 'temp-customer-id',
          companyName: user.name || 'Your Company',
          contactEmail: user.email,
        };
        setOnboardingData(dynamicOnboardingData);
        localStorage.setItem('onboardingData', JSON.stringify(dynamicOnboardingData));
      } else {
        showError('Unable to determine company information. Please contact support.');
        // Clear auth data and redirect to login
        localStorage.removeItem('onboardingData');
        navigate('/login');
      }
    }
  }, [navigate, showError, user]);

  // Handle step parameter from URL
  useEffect(() => {
    const stepParam = searchParams.get('step');
    if (stepParam) {
      const step = parseInt(stepParam, 10);
      if (step >= 0 && step < steps.length) {
        setActiveStep(step);
      }
    }
  }, [searchParams, steps.length]);

  // Fetch plans and current subscription from API; store MY_SUBSCRIPTION response in localStorage
  useEffect(() => {
    const loadData = async () => {
      if (DISABLE_SUBSCRIPTION || plansLoading || plans.length > 0) return;
      
      setPlansLoading(true);
      setPlansError(null);
      
      try {
        // Fetch plans
        const plansResponse = await fetchPlans();
        if (plansResponse.success) {
          setPlans(plansResponse.data);
        } else {
          setPlansError(plansResponse.message || 'Failed to fetch plans');
        }

        // Fetch MY_SUBSCRIPTION when subscription enabled and we have auth token
        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) || onboardingData?.accessToken;
        if (!DISABLE_SUBSCRIPTION && token && !hasFetchedSubscription.current) {
          setSubscriptionLoading(true);
          try {
            const subscriptionResponse = await getMySubscription(token);
            // Store full MY_SUBSCRIPTION response in localStorage for PlansCard (current plan label, zohoSubscriptionId for cancel)
            if (subscriptionResponse.success && subscriptionResponse.data) {
              // Store MY_SUBSCRIPTION response.data only (plan + subscription fields)
              setMySubscriptionData({ ...subscriptionResponse.data } as MySubscriptionStoredData);
              // Update onboarding state with current subscription (only when we have onboardingData)
              if (onboardingData) {
                const updatedData: OnboardingData = {
                  ...onboardingData,
                  planId: subscriptionResponse.data.id,
                  planName: subscriptionResponse.data.name,
                  planCode: subscriptionResponse.data.planCode,
                  // Preserve isDefaultPlan:true for users still in their initial signup
                  // session — auto-assigned free plan should not count as a consciously
                  // chosen plan. Only override to false for returning users (no session flag).
                  isDefaultPlan: sessionStorage.getItem('isNewSignup') === 'true' ? true : false,
                };
                setOnboardingData(updatedData);
                localStorage.setItem('onboardingData', JSON.stringify(updatedData));
              }
              setSelectedPlan(subscriptionResponse.data.id);
            }
          } catch (subscriptionError: unknown) {
            console.warn('Failed to fetch subscription:', subscriptionError);
          } finally {
            setSubscriptionLoading(false);
            hasFetchedSubscription.current = true;
          }
        }
      } catch (error: unknown) {
        const err = error as Error;
        console.error('Error fetching data:', err);
        setPlansError(err.message || 'Failed to fetch data');
      } finally {
        setPlansLoading(false);
      }
    };

    loadData();
  }, [plansLoading, plans.length, onboardingData?.accessToken, onboardingData]);

  // Sync selected plan from current subscription (prefer planCode for consistency)
  useEffect(() => {
    if (!plansLoading && plans.length > 0 && onboardingData) {
      const currentPlanCode = getMySubscriptionData()?.planCode ?? onboardingData.planCode;
      const currentPlan = currentPlanCode
        ? plans.find((plan) => plan.planCode === currentPlanCode)
        : plans.find((plan) => plan.id === onboardingData.planId);
      if (currentPlan) {
        setSelectedPlan(currentPlan.id);
      }
    }
  }, [plansLoading, plans, onboardingData]);

  const handleNext = async () => {
    // From Welcome when subscription disabled: go to Connect Chat service (step 1)
    if (DISABLE_SUBSCRIPTION && activeStep === 0) {
      setActiveStep(1);
      return;
    }
    // From Connect Chat service (step 1) when subscription disabled: go to supportSpace
    if (DISABLE_SUBSCRIPTION && activeStep === 1) {
      navigate('/supportSpace');
      return;
    }
    if (activeStep === 1 && !selectedPlan) {
      showError('Please select a plan to continue');
      return;
    }

    if (activeStep === 1 && selectedPlan) {
      await handlePlanSelection();
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handlePlanSelect = (planId: string) => {
    setSelectedPlan(planId);
  };

  const handlePlanSelection = async () => {
    if (!onboardingData || !selectedPlan) {
      showError('Missing required data for plan selection');
      return;
    }

    const selectedPlanDetails = plans.find(plan => plan.id === selectedPlan);
    if (!selectedPlanDetails) {
      showError('Selected plan not found');
      return;
    }

    try {
      // Use planCode to decide: only redirect to payment when selected plan is different from current plan
      const currentPlanCode = getMySubscriptionData()?.planCode ?? onboardingData?.planCode;
      const isPlanChanged =
        !!currentPlanCode &&
        !!selectedPlanDetails.planCode &&
        currentPlanCode !== selectedPlanDetails.planCode;

      if (isPlanChanged) {
        // Use the common plan upgrade service for plan changes (email fallback: onboarding may not have contactEmail set)
        const contactEmail =
          onboardingData.contactEmail ||
          user?.email ||
          getLoggedInUserData()?.email_id ||
          '';
        const upgradeRequest = {
          planId: selectedPlan,
          planName: selectedPlanDetails.name,
          planCode: selectedPlanDetails.planCode,
          customerId: onboardingData.customerId,
          customerEmail: contactEmail,
          customerCompanyName: onboardingData.companyName,
          isPlanChange: true
        };

        const response = await upgradePlan(upgradeRequest);

        if (response.success && response.hostedPageUrl) {
          showSuccess('Redirecting to payment gateway...');
          // User is consciously choosing a paid plan — clear the new-signup marker before
          // leaving so the badge is suppressed correctly if they return to /onboarding.
          sessionStorage.removeItem('isNewSignup');
          window.location.href = response.hostedPageUrl;
          return; // Don't proceed to next step, user will be redirected
        } else {
          throw new Error(response.error || 'Failed to create payment page');
        }
      } else {
        showSuccess('Plan selected successfully!');
      }
      
      // Update onboarding data
      const updatedData = {
        ...onboardingData,
        planId: selectedPlan,
        planName: selectedPlanDetails.name,
        planCode: selectedPlanDetails.planCode
      };
      setOnboardingData(updatedData);
      localStorage.setItem('onboardingData', JSON.stringify(updatedData));

      // User has consciously chosen a plan — clear the new-signup session marker so
      // returning to /onboarding later shows the "Current Plan" badge correctly.
      sessionStorage.removeItem('isNewSignup');

      // No further steps: redirect to support space (no step 2 in this flow)
      navigate('/supportSpace');
      return;
    } catch (error: any) {
      console.error('Error selecting plan:', error);
      showError(error.message || 'Failed to select plan. Please try again.');
    }
  };


  const handleLaunchApp = () => {
    const appLink = window.location.origin; // TODO: Replace with APP_CONFIG.APP_URL when available
    window.location.href = appLink;
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" component="h2" sx={{ mb: 3, color: 'text.secondary' }}>
              Thank you for choosing <Box component="strong" sx={{ color: 'primary.main' }}>{APP_NAME}</Box>
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Your company <Box component="strong" sx={{ color: 'primary.main' }}>{onboardingData?.companyName}</Box> has been successfully created.
              Let's complete your setup in just a few steps.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleNext}
              endIcon={<ArrowForward />}
              sx={{ 
                py: 1.5,
                px: 4,
                textTransform: 'none',
                fontSize: '1.1rem',
              }}
            >
              Get Started
            </Button>
          </Box>
        );

      case 1:
        // Connect Chat service step when subscription disabled; otherwise Choose Plan
        if (DISABLE_SUBSCRIPTION) {
          const companyId =
            onboardingData?.companyId ||
            localStorage.getItem('companyId') ||
            getCompanyId();
          return (
            <Box sx={{ py: 4 }}>
              <ChatService
                companyId={companyId || ''}
                onComplete={() => navigate('/supportSpace')}
                hideBottomActions={true}
                onTokensChange={setHasChatServiceToken}
                mode="onboarding"
              />
            </Box>
          );
        }
        return (
          <Box sx={{ py: 4 }}>
            <Typography variant="h4" component="h2" sx={{ mb: 3, textAlign: 'center' }}>
              Choose Your Plan
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
              Select the plan that best fits your needs
            </Typography>
            
            {plansLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : plansError ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {plansError}
              </Alert>
            ) : (
              <PlansCard
                plans={plans}
                selectedPlan={selectedPlan}
                // New users arriving from signup have not consciously chosen a plan yet;
                // suppress the "Current Plan" badge so the auto-assigned default
                // is shown only as a pre-selected default, not as an owned plan.
                currentPlanCode={isNewUserOnboarding ? undefined : (getMySubscriptionData()?.planCode ?? onboardingData?.planCode)}
                currentPlanId={isNewUserOnboarding ? undefined : (getMySubscriptionData()?.id ?? onboardingData?.planId)}
                onPlanSelect={handlePlanSelect}
                showCurrentPlanBadge={!isNewUserOnboarding}
                showSelection={true}
                // New users have no Zoho subscription ID yet (free plan), so force buttons on
                // so "Upgrade to this plan" renders. isCurrentPlan=false for all cards (from above),
                // so "Cancel Subscription" will never appear for new users.
                showActionButtons={isNewUserOnboarding ? true : !!getZohoSubscriptionId()}
                subscriptionId={getZohoSubscriptionId() || undefined}
                platformName={PLATFORM_NAME}
                subscriptionSyncVersion={subscriptionUiVersion}
                onCancelSuccess={async () => {
                  // Refresh subscription from API and keep mySubscription in localStorage so cancellation banner can show
                  const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) || onboardingData?.accessToken;
                  if (token) {
                    try {
                      const subscriptionResponse = await getMySubscription(token);
                      if (subscriptionResponse?.success && subscriptionResponse?.data) {
                        setMySubscriptionData({ ...subscriptionResponse.data } as MySubscriptionStoredData);
                        // Notify Layout to refresh banner state so cancellation banner shows immediately
                        window.dispatchEvent(new CustomEvent('subscription-updated'));
                      }
                    } catch (e) {
                      console.warn('Failed to refresh subscription after cancel:', e);
                    }
                  }
                  setOnboardingData((prev) => prev ? { ...prev, planId: undefined, planName: undefined, planCode: undefined } : null);
                  setSelectedPlan('');
                }}
                onReactivateSuccess={async () => {
                  // Same refresh as cancel but keep selected plan — status should return to active
                  const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) || onboardingData?.accessToken;
                  if (token) {
                    try {
                      const subscriptionResponse = await getMySubscription(token);
                      if (subscriptionResponse?.success && subscriptionResponse?.data) {
                        setMySubscriptionData({ ...subscriptionResponse.data } as MySubscriptionStoredData);
                        window.dispatchEvent(new CustomEvent('subscription-updated'));
                      }
                    } catch (e) {
                      console.warn('Failed to refresh subscription after reactivate:', e);
                    }
                  }
                  setSubscriptionUiVersion((v) => v + 1);
                }}
                gridColumns={{ xs: 1, md: 2, lg: 3 }}
              />
            )}
          </Box>
        );


      default:
        return 'Unknown step';
    }
  };

  if (!onboardingData) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'background.default',
      py: 4,
    }}>
      <Container maxWidth="md">
        <Paper 
          elevation={3} 
          sx={{ 
            borderRadius: 2,
            backgroundColor: 'background.paper',
          }}
        >
          <Box sx={{ p: 3, borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}` }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Box sx={{ p: 4 }}>
            {getStepContent(activeStep)}
          </Box>

          {activeStep !== 0 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              p: 3, 
              borderTop: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`
            }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none' }}
              >
                Back
              </Button>
              
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={
                  activeStep === 1 && DISABLE_SUBSCRIPTION
                    ? !hasChatServiceToken
                    : activeStep === 1 && !DISABLE_SUBSCRIPTION
                    ? !selectedPlan
                    : false
                }
                endIcon={<ArrowForward />}
                sx={{ textTransform: 'none' }}
              >
                {activeStep === 1 && !DISABLE_SUBSCRIPTION
                  ? 'Continue with this plan'
                  : activeStep === 1 && DISABLE_SUBSCRIPTION
                  ? 'Continue'
                  : 'Next'}
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Onboarding;
